import type { WinterMonthlyWeather } from '@/cms/fields/types/content-page'
import type { SnowTier } from '@/scopes/content/domain/weather'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { format } from 'date-fns'
import { useIntl } from '@/intl'
import { useDateFnsLocale } from '@/scopes/common/hooks/useDateFnsLocale'
import { WeatherGraphColumn, WeatherGraphColumnHeading } from './WeatherGraph'
import { WeatherGraphBar } from './WeatherGraphBar'
import styles from './WinterWeatherGraphColumn.module.css'

interface WinterWeatherGraphColumnProps {
  data: WinterMonthlyWeather
  maxSnowDepthCm: number
  tier: SnowTier
}

const TIER_CLASS: Record<SnowTier, string> = {
  1: styles.high,
  2: styles.medium,
  3: styles.low,
}

export function WinterWeatherGraphColumn({
  data,
  maxSnowDepthCm,
  tier,
}: WinterWeatherGraphColumnProps) {
  const intl = useIntl()
  const dateFnsLocale = useDateFnsLocale()
  const snowDepthRatio = data.snowDepthTopCm / maxSnowDepthCm
  const monthName = format(new Date(2026, data.month - 1, 1), 'MMM', { locale: dateFnsLocale })
    .replace('.', '')

  return (
    <WeatherGraphColumn>
      <WeatherGraphColumnHeading className={styles.heading}>
        <Typography variant="body-l-regular-tight">{monthName}</Typography>
      </WeatherGraphColumnHeading>
      <WeatherGraphBar className={cn(styles.winter, TIER_CLASS[tier])} ratio={snowDepthRatio}>
        <SnowDepth valueCm={data.snowDepthTopCm} />
        <SnowDepth valueCm={data.snowDepthBottomCm} />
      </WeatherGraphBar>
      <div className={styles.snowDays}>
        <Icon glyph="Snowflake" />
        <Typography variant="body-l-bold-tight">{Math.round(data.snowDays)}</Typography>
        <Typography variant="body-s-regular-tight">
          {intl.formatMessage({ id: 'content_weather_label_days' })}
        </Typography>
      </div>
    </WeatherGraphColumn>
  )
}

function SnowDepth({ valueCm }: { valueCm: number }) {
  return (
    <div className={styles.snowHeight}>
      <Typography variant="body-l-bold-tight">{Math.round(valueCm)}</Typography>
      <Typography variant="body-s-regular-tight">cm</Typography>
    </div>
  )
}
